import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import { Add, Edit, Delete, FlagOutlined } from '@mui/icons-material';
import { useGetFeaturesQuery } from '@/state/queries/features/use-get-features';
import { useDeleteFeatureMutation } from '@/state/mutations/features/use-delete-feature';
import { useTranslations } from '@/i18n/use-translations';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import ConfirmationModal from '@/components/common/confirmation-modal';
import FeatureFormModal from './feature-form.modal';
import type { Feature } from '@/types/features';

export default function FeaturesPage() {
  const t = useTranslations();
  const { data: features, isLoading, error } = useGetFeaturesQuery();
  const deleteMutation = useDeleteFeatureMutation();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [featurePendingDelete, setFeaturePendingDelete] = useState<Feature | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleConfirmDelete = async () => {
    if (!featurePendingDelete?.id) return;
    const id = featurePendingDelete.id;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(id);
      setFeaturePendingDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete feature');
    }
  };

  return (
    <OrganizationContentStack
      content={
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <PageHeaderBar
            mainContent={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <FlagOutlined sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('features.title') || 'Features'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => {
                    setEditingFeature(null);
                    setCreateModalOpen(true);
                  }}
                  sx={{ flexShrink: 0 }}
                >
                  {t('features.create') || 'Create Feature'}
                </Button>
              </Box>
            }
          />

          <Card
            sx={{
              mt: 2,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <CardContent
              sx={{
                'flex': 1,
                'minHeight': 0,
                'display': 'flex',
                'flexDirection': 'column',
                'overflow': 'hidden',
                'p': 0,
                '&:last-child': {
                  pb: 0,
                },
              }}
            >
              {isLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography>{t('common.loading') || 'Loading...'}</Typography>
                </Box>
              ) : error ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="error">
                    {t('features.error') || 'Error loading features. Make sure the backend server is running.'}
                  </Typography>
                </Box>
              ) : !features || features.length === 0 ? (
                <Box sx={{ p: 4 }}>
                  <EmptyState
                    text={t('features.empty.title') || 'No features yet'}
                    icon={<FlagOutlined sx={{ fontSize: 48, color: 'text.secondary' }} />}
                  />
                </Box>
              ) : (
                <TableContainer
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Display Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Enabled Roles</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Enabled For All</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>A/B Testing</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {features.map((feature) => (
                        <TableRow key={feature.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {feature.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{feature.displayName}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                              {feature.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {feature.enabledRoles.length > 0 ? (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {feature.enabledRoles.map((role) => (
                                  <Chip key={role} label={role} size="small" />
                                ))}
                              </Stack>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {feature.enabledForAll ? (
                              <Chip label="Yes" color="success" size="small" />
                            ) : (
                              <Chip label="No" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {feature.abTesting?.enabled ? (
                              <Chip label={`${feature.abTesting.percentage}%`} color="info" size="small" />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                size="small"
                                onClick={() => setEditingFeature(feature)}
                                aria-label="Edit feature"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDeleteError(null);
                                  setFeaturePendingDelete(feature);
                                }}
                                aria-label="Delete feature"
                                color="error"
                                disabled={deleteMutation.isPending}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <ConfirmationModal
            open={Boolean(featurePendingDelete)}
            isLoading={deleteMutation.isPending}
            title={t('common.delete')}
            text={
              deleteError
                ? `${t('features.confirmDelete', { name: featurePendingDelete?.name ?? '' })} — ${deleteError}`
                : t('features.confirmDelete', { name: featurePendingDelete?.name ?? '' })
            }
            confirmLabel={t('common.delete')}
            onConfirm={() => {
              void handleConfirmDelete();
            }}
            onCancel={() => {
              if (deleteMutation.isPending) return;
              setDeleteError(null);
              setFeaturePendingDelete(null);
            }}
          />

          <FeatureFormModal
            open={createModalOpen}
            onClose={() => {
              setCreateModalOpen(false);
              setEditingFeature(null);
            }}
            feature={null}
          />

          {editingFeature && (
            <FeatureFormModal
              open={!!editingFeature}
              onClose={() => {
                setEditingFeature(null);
                setCreateModalOpen(false);
              }}
              feature={editingFeature}
            />
          )}
        </Box>
      }
    />
  );
}
